import { DialogActionsStacking } from 'omakase';
import { DialogEntranceMotion, DialogVariant } from './DialogCore';
import { Theme, css } from '@emotion/react';
import { assertNever } from '@tock/shared/utils/typeUtils';
import {
  tockLogoHeadingWidthDesktopPx,
  tockLogoHeadingWidthPx,
} from 'omakase/TockLogo/tockLogoCssDefs';

export const HEADING_HEIGHT_MOBILE = 68;
export const HEADING_HEIGHT_DESKTOP = 84;
export const ACTIONS_HEIGHT_MOBILE = 80;
export const ACTIONS_HEIGHT_DESKTOP = 84;

interface DialogContainerStyleOptions {
  isDesktop: boolean;
  fullHeight: boolean;
  maxContent: boolean;
}

const getContainerStylesFromType = (
  dialogVariant: DialogVariant,
  entranceMotion: DialogEntranceMotion
) => {
  const styles = [];
  switch (dialogVariant) {
    case 'partial':
      styles.push(css`
        .MuiDialog-paper {
          height: 75%;
        }
      `);
      switch (entranceMotion) {
        case 'down':
          styles.push(css`
            .MuiDialog-paper {
              align-self: flex-start;
            }
          `);
          break;
        case 'up':
          styles.push(css`
            .MuiDialog-paper {
              align-self: flex-end;
            }
          `);
          break;
      }
      break;
    case 'sheet':
      styles.push(css`
        .MuiDialog-paper {
          height: 50%;
        }
      `);
      switch (entranceMotion) {
        case 'down':
          styles.push(css`
            .MuiDialog-paper {
              align-self: flex-start;
            }
          `);
          break;
        case 'up':
          styles.push(css`
            .MuiDialog-paper {
              align-self: flex-end;
            }
          `);
          break;
      }
      break;
    case 'centered':
      break;
    case 'fullScreen':
      break;
    default:
      assertNever(dialogVariant);
  }
  return styles;
};

const getPaperHeightStyles = ({ fullHeight, maxContent }: DialogContainerStyleOptions) => css`
  ${maxContent ? 'height: max-content;' : ''}
  ${fullHeight ? 'height: 100%;' : ''}
`;

export const base = (
  { colors }: Theme,
  dialogType: DialogVariant,
  entranceMotion: DialogEntranceMotion,
  containerOptions: DialogContainerStyleOptions
) => css`
  .MuiDialog-container {
    // https://stackoverflow.com/questions/66204563/backdrop-filter-not-working-when-mix-blend-mode-of-another-element-on-the-page-i
    // Without this declaration, a child element cannot properly render using mix-blend-mode: multiply
    backdrop-filter: opacity(1);
  }

  .MuiDialog-paper {
    border-radius: 0;
  }

  ${getContainerStylesFromType(dialogType, entranceMotion)}

  .MuiDialog-paper {
    ${getPaperHeightStyles(containerOptions)}
  }

  .MuiBackdrop-root {
    background: ${colors.bgOverlay};
    backdrop-filter: blur(6px);
  }
`;

/* In the sidepanel case, where we have a `display: flex;` style on dialogContent
 * setting a min-height is also necessary to prevent the heading from being squashed down
 * to 28px. For some reason. */
export const dialogHeading = ({
  boxShadows,
  breakpoints,
  colors,
  spacing,
  timings,
  typography,
  zIndex,
}: Theme) => css`
  align-items: center;
  background-color: ${colors.bgDefault};
  box-shadow: ${boxShadows.dropShadowNone};
  border-bottom: 1px solid ${colors.borderDefault};
  display: flex;
  flex-shrink: 0;
  height: ${HEADING_HEIGHT_MOBILE}px;
  justify-content: center;
  padding: 0 ${spacing(1)}px;
  position: relative;
  transition: box-shadow ${timings.duration.standard} ${timings.timingFunction.easeInOut};
  width: 100%;
  z-index: ${zIndex.header};
  ${typography.variant('heading04')}

  ${breakpoints.up('sm')} {
    height: ${HEADING_HEIGHT_DESKTOP}px;
    padding: 0 ${spacing(2)}px;
  }
`;

export const dialogHeadingShadow = ({ shadow }: Theme) => css`
  box-shadow: ${shadow[100]};
`;

export const dialogHeadingCenterElement = () => css`
  position: absolute;
  top: 50%;
  left: 50%;
  text-align: center;
  transform: translate(-50%, -50%);
  width: 75%;
`;

export const dialogHeadingCenterElementTitle = () => css`
  overflow: hidden;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const dialogHeadingRightElement = ({ spacing }: Theme) => css`
  margin-left: auto;

  /* Width of medium ghost button - ensures title ellipsis begin at correct place */
  width: 44px;
`;

export const dialogHeadingLeftElementArrow = ({ spacing }: Theme) => css`
  margin-right: auto;
`;

export const dialogHeadingLeftElementLogo = ({ breakpoints, colors, spacing }: Theme) => css`
  color: ${colors.bgPrimaryBase};
  margin-left: ${spacing(1.5)}px;
  margin-right: auto;
  width: ${tockLogoHeadingWidthPx};

  ${breakpoints.up('sm')} {
    width: ${tockLogoHeadingWidthDesktopPx};
  }
`;

export const dialogHeadingLeftElementTitle = ({ spacing }: Theme) => css`
  margin-left: ${spacing(1.5)}px;
  margin-right: auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const dialogContent = (
  { breakpoints, spacing }: Theme,
  { disablePadding }: { disablePadding: boolean }
) => css`
  flex: 1;

  ${!disablePadding &&
  css`
    padding: ${spacing(2.5)}px;
    ${breakpoints.up('sm')} {
      padding: ${spacing(3.5)}px;
    }
  `}

  ${disablePadding &&
  css`
    padding: 0;
  `}
`;

interface DialogActionsOptions {
  stack: DialogActionsStacking;
}

const getActionsStackingStyles = (
  { breakpoints, spacing }: Theme,
  { stack }: DialogActionsOptions
) => {
  switch (stack) {
    case true:
      return css`
        flex-direction: column-reverse;
        padding: ${spacing(2.25)}px ${spacing(2.5)}px
          calc(${spacing(2.25)}px + env(safe-area-inset-bottom));

        & > * + * {
          margin-bottom: ${spacing(2)}px;
        }

        ${breakpoints.up('sm')} {
          padding: ${spacing(2.25)}px ${spacing(3.5)}px
            calc(${spacing(2.25)}px + env(safe-area-inset-bottom));
        }
      `;
    case 'mobile':
      return css`
        flex-direction: column-reverse;
        padding: ${spacing(2.25)}px ${spacing(2.5)}px
          calc(${spacing(2.25)}px + env(safe-area-inset-bottom));

        & > * + * {
          margin-bottom: ${spacing(2)}px;
        }

        ${breakpoints.up('sm')} {
          flex-direction: row;
          padding: 0 ${spacing(3.5)}px env(safe-area-inset-bottom);
          & > * + * {
            margin-bottom: initial;
            margin-left: ${spacing(2)}px;
          }
        }
      `;
    case false:
      return css`
        flex-direction: row;
        padding: 0 ${spacing(2.5)}px env(safe-area-inset-bottom);

        ${breakpoints.up('sm')} {
          padding: 0 ${spacing(3.5)}px env(safe-area-inset-bottom);
        }

        & > * + * {
          margin-left: ${spacing(2)}px;
        }
      `;
    default:
      assertNever(stack);
  }
};

export const dialogActions = (theme: Theme, options: DialogActionsOptions) => css`
  align-items: center;
  background-color: ${theme.colors.bgDefault};
  border-top: 1px solid ${theme.colors.borderDefault};
  display: flex;
  flex-shrink: 0;
  min-height: calc(${ACTIONS_HEIGHT_MOBILE}px + env(safe-area-inset-bottom));
  justify-content: flex-end;
  width: 100%;

  ${theme.breakpoints.up('sm')} {
    min-height: ${ACTIONS_HEIGHT_DESKTOP}px;
  }

  ${getActionsStackingStyles(theme, options)};
`;

export const dialogForm = () => css`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: auto;
`;
